import React, { useState, useEffect } from 'react';
import { 
  Headphones,
  Clock, 
  Users, 
  CheckCircle,
  AlertCircle,
  Camera,
  CameraOff,
  Mic,
  MicOff,
  Upload,
  FileText,
  Phone,
  MessageCircle,
  Shield,
  Monitor,
  Loader,
  Video,
  Settings,
  CreditCard,
  Package
} from 'lucide-react';
import { BrandingProvider } from '../../contexts/BrandingContext';
import { 
  BrandedHeader, 
  BrandedButton, 
  BrandedCard, 
  BrandedInput, 
  BrandedSelect, 
  BrandedTextarea,
  BrandedBadge,
  BrandedAlert 
} from '../../components/branding/BrandedComponents';

interface CustomerWaitingRoomProps {
  companyName?: string;
  companyLogo?: string;
  organizationId?: string;
  ticketId?: string; // Pass ticket ID from URL params
}

interface TicketStatus {
  ticketId: string;
  status: 'new' | 'in_progress' | 'waiting' | 'resolved' | 'closed';
  customerName: string;
  email: string;
  category: string;
  priority: string;
  subject: string;
  description: string;
  createdAt: string;
  assignedTo?: string;
  waitTime: number;
  meetingId?: string;
  estimatedResponseTime?: string;
}

export const CustomerWaitingRoom: React.FC<CustomerWaitingRoomProps> = ({
  companyName = 'Support Center',
  companyLogo,
  organizationId = 'org_1754194082893_o02h6k',
  ticketId
}) => {
  return (
    <BrandingProvider templateType="business" initialBranding={{ companyName, companyLogo }}>
      <CustomerWaitingRoomContent 
        companyName={companyName}
        companyLogo={companyLogo}
        organizationId={organizationId}
        ticketId={ticketId}
      />
    </BrandingProvider>
  );
};

const CustomerWaitingRoomContent: React.FC<CustomerWaitingRoomProps> = ({
  companyName = 'Support Center',
  companyLogo,
  organizationId = 'org_1754194082893_o02h6k',
  ticketId
}) => {
  const [ticketStatus, setTicketStatus] = useState<TicketStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionTest, setConnectionTest] = useState<'testing' | 'success' | 'failed'>('testing');
  const [cameraPermission, setCameraPermission] = useState<'unknown' | 'granted' | 'denied'>('unknown');
  const [micPermission, setMicPermission] = useState<'unknown' | 'granted' | 'denied'>('unknown');

  // Get ticket ID from URL if not provided as prop
  const currentTicketId = ticketId || window.location.pathname.split('/').pop();

  // Test camera and microphone permissions
  useEffect(() => {
    const testMediaDevices = async () => {
      try {
        setConnectionTest('testing');
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        setCameraPermission('granted');
        setMicPermission('granted');
        setConnectionTest('success');
        // Stop the stream
        stream.getTracks().forEach(track => track.stop());
      } catch (err) {
        console.error('Media device test failed:', err);
        setCameraPermission('denied');
        setMicPermission('denied');
        setConnectionTest('failed');
      }
    };

    testMediaDevices();
  }, []);

  // Poll ticket status
  useEffect(() => {
    if (!currentTicketId) {
      setError('No ticket ID provided');
      setIsLoading(false);
      return;
    }

    console.log('🔄 CUSTOMER: Starting status polling for ticket ID:', currentTicketId);
    
    const pollStatus = async () => {
      try {
        console.log('🔍 CUSTOMER: Polling status for ticket ID:', currentTicketId);
        const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/tickets/${currentTicketId}/status`);
        
        if (response.ok) {
          const statusData = await response.json();
          console.log('📊 CUSTOMER: Status update received:', statusData);
          setTicketStatus(statusData.ticket);
          
          // Debug: Check redirect conditions in detail
          console.log('🔍 CUSTOMER: Checking redirect conditions:', {
            status: statusData.ticket.status,
            statusCheck: statusData.ticket.status === 'in_progress',
            assignedTo: statusData.ticket.assignedTo,
            assignedToCheck: !!statusData.ticket.assignedTo,
            meetingId: statusData.ticket.meetingId,
            meetingIdCheck: !!statusData.ticket.meetingId,
            shouldRedirect: statusData.ticket.status === 'in_progress' && statusData.ticket.assignedTo && statusData.ticket.meetingId
          });
          
          // Check if ticket has been accepted and has agent assigned
          if (statusData.ticket.status === 'in_progress' && statusData.ticket.assignedTo && statusData.ticket.meetingId) {
            console.log('✅ CUSTOMER: Ticket accepted by agent! Attempting to join video session...');
            // Auto-redirect to video session
            await joinVideoSession(statusData.ticket);
          }
        } else {
          console.warn('⚠️ CUSTOMER: Status polling failed with status:', response.status);
          const errorData = await response.text();
          setError(`Failed to get ticket status: ${errorData}`);
        }
      } catch (err) {
        console.error('🔴 CUSTOMER: Error polling status:', err);
        setError(err instanceof Error ? err.message : 'Failed to check ticket status');
      } finally {
        setIsLoading(false);
      }
    };

    // Initial status check
    pollStatus();
    
    // Poll every 5 seconds
    const interval = setInterval(pollStatus, 5000);
    
    return () => {
      console.log('🛑 CUSTOMER: Cleaning up status polling...');
      clearInterval(interval);
    };
  }, [currentTicketId]);

  const joinVideoSession = async (ticket: TicketStatus) => {
    try {
      console.log('🎬 CUSTOMER: Joining video session for ticket:', ticket.ticketId);
      
      // Extract org ID from current URL if present (same pattern as AgentDashboard)
      const pathname = window.location.pathname;
      const orgMatch = pathname.match(/\/(org_[a-zA-Z0-9_]+)\//);
      const orgPrefix = orgMatch ? `/${orgMatch[1]}` : '';
      
      // Build customer join URL with org prefix
      const baseUrl = window.location.origin;
      const customerSessionPath = `/templates/support/session/customer?roomId=${ticket.meetingId}&ticketId=${ticket.ticketId}&customerName=${encodeURIComponent(ticket.customerName)}&agentName=${encodeURIComponent(ticket.assignedTo || 'Support Agent')}&subject=${encodeURIComponent(ticket.subject)}&category=${ticket.category}&priority=${ticket.priority}`;
      const customerJoinUrl = `${baseUrl}${orgPrefix}${customerSessionPath}`;
      
      console.log('🔗 CUSTOMER: Redirecting to video session:', {
        orgPrefix,
        sessionPath: customerSessionPath,
        finalUrl: customerJoinUrl,
        pathname
      });
      
      // Redirect to video session
      window.location.href = customerJoinUrl;
      
    } catch (err) {
      console.error('🔴 CUSTOMER: Error joining video session:', err);
      setError('Failed to join video session. Please try again.');
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'technical': return <Settings size={20} />;
      case 'billing': return <CreditCard size={20} />;
      case 'product': return <Package size={20} />;
      case 'general': return <MessageCircle size={20} />;
      default: return <MessageCircle size={20} />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const getEstimatedWaitTime = (priority: string, waitTime: number) => {
    const baseTime = priority === 'urgent' ? 60 : priority === 'high' ? 240 : priority === 'medium' ? 1440 : 4320; // minutes
    const remainingTime = Math.max(0, baseTime - waitTime);
    
    if (remainingTime === 0) return 'Any moment now';
    if (remainingTime < 60) return `${remainingTime} minutes`;
    if (remainingTime < 1440) return `${Math.ceil(remainingTime / 60)} hours`;
    return `${Math.ceil(remainingTime / 1440)} days`;
  };

  if (error && !ticketStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--brand-background)' }}>
        <BrandedCard className="max-w-md w-full text-center">
          <AlertCircle size={48} className="mx-auto mb-4" style={{ color: 'var(--brand-error)' }} />
          <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--brand-text-primary)' }}>
            Unable to Load Ticket
          </h2>
          <p className="mb-6" style={{ color: 'var(--brand-text-secondary)' }}>
            {error}
          </p>
          <BrandedButton
            onClick={() => window.location.href = '/templates/support/customer'}
            variant="primary"
          >
            Submit New Ticket
          </BrandedButton>
        </BrandedCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--brand-background)' }}>
      {/* Header */}
      <header className="border-b" style={{ borderColor: 'var(--brand-border)' }}>
        <div className="max-w-4xl mx-auto px-4 py-6">
          <BrandedHeader 
            showLogo={true} 
            showTagline={true}
            customTagline="We're here to help you"
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Ticket Status */}
          <div className="lg:col-span-2">
            <BrandedCard className="mb-6">
              <div className="flex items-center mb-6">
                <Headphones size={24} className="mr-3" style={{ color: 'var(--brand-primary)' }} />
                <h2 className="text-xl font-semibold" style={{ color: 'var(--brand-text-primary)' }}>
                  Support Request Status
                </h2>
              </div>

              {isLoading && !ticketStatus ? (
                <div className="text-center py-8">
                  <Loader className="animate-spin mx-auto mb-4" size={32} style={{ color: 'var(--brand-primary)' }} />
                  <p style={{ color: 'var(--brand-text-secondary)' }}>Loading your ticket status...</p>
                </div>
              ) : ticketStatus ? (
                <div className="space-y-6">
                  {/* Ticket Info */}
                  <div className="border rounded-lg p-4" style={{ borderColor: 'var(--brand-border)' }}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          {getCategoryIcon(ticketStatus.category)}
                          <h3 className="font-semibold" style={{ color: 'var(--brand-text-primary)' }}>
                            {ticketStatus.subject}
                          </h3>
                          <BrandedBadge variant={getPriorityColor(ticketStatus.priority)} size="small">
                            {ticketStatus.priority.toUpperCase()}
                          </BrandedBadge>
                        </div>
                        <p className="text-sm mb-2" style={{ color: 'var(--brand-text-secondary)' }}>
                          Ticket #{ticketStatus.ticketId}
                        </p>
                        <p className="text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                          {ticketStatus.description}
                        </p>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="border-t pt-4" style={{ borderColor: 'var(--brand-border)' }}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {ticketStatus.status === 'new' ? (
                            <>
                              <Clock size={16} style={{ color: 'var(--brand-warning)' }} />
                              <span className="font-medium" style={{ color: 'var(--brand-text-primary)' }}>
                                Waiting for Agent
                              </span>
                            </>
                          ) : ticketStatus.status === 'in_progress' && ticketStatus.assignedTo ? (
                            <>
                              <Video size={16} style={{ color: 'var(--brand-success)' }} />
                              <span className="font-medium" style={{ color: 'var(--brand-text-primary)' }}>
                                Agent Assigned: {ticketStatus.assignedTo}
                              </span>
                            </>
                          ) : (
                            <>
                              <Users size={16} style={{ color: 'var(--brand-primary)' }} />
                              <span className="font-medium" style={{ color: 'var(--brand-text-primary)' }}>
                                {ticketStatus.status.charAt(0).toUpperCase() + ticketStatus.status.slice(1).replace('_', ' ')}
                              </span>
                            </>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                            Wait time: {ticketStatus.waitTime} minutes
                          </p>
                          {ticketStatus.status === 'new' && (
                            <p className="text-xs" style={{ color: 'var(--brand-text-muted)' }}>
                              Est. response: {getEstimatedWaitTime(ticketStatus.priority, ticketStatus.waitTime)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status Updates */}
                  {ticketStatus.status === 'new' ? (
                    <BrandedAlert variant="info">
                      <div className="flex items-center">
                        <Clock size={16} className="mr-2" />
                        <span>
                          Your ticket is in the queue. Our support team will be with you shortly.
                        </span>
                      </div>
                    </BrandedAlert>
                  ) : ticketStatus.status === 'in_progress' ? (
                    <BrandedAlert variant="success">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="mr-2" />
                        <span>
                          {ticketStatus.assignedTo} has accepted your ticket and will join you in the video session momentarily.
                        </span>
                      </div>
                    </BrandedAlert>
                  ) : null}
                </div>
              ) : null}
            </BrandedCard>

            {/* Connection Test Results */}
            <BrandedCard>
              <h3 className="font-semibold mb-4" style={{ color: 'var(--brand-text-primary)' }}>
                Video Call Readiness
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {cameraPermission === 'granted' ? <Camera size={16} /> : <CameraOff size={16} />}
                    <span className="text-sm">Camera Access</span>
                  </div>
                  <BrandedBadge 
                    variant={cameraPermission === 'granted' ? 'success' : cameraPermission === 'denied' ? 'error' : 'secondary'}
                    size="small"
                  >
                    {cameraPermission === 'granted' ? 'Ready' : cameraPermission === 'denied' ? 'Blocked' : 'Testing...'}
                  </BrandedBadge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {micPermission === 'granted' ? <Mic size={16} /> : <MicOff size={16} />}
                    <span className="text-sm">Microphone Access</span>
                  </div>
                  <BrandedBadge 
                    variant={micPermission === 'granted' ? 'success' : micPermission === 'denied' ? 'error' : 'secondary'}
                    size="small"
                  >
                    {micPermission === 'granted' ? 'Ready' : micPermission === 'denied' ? 'Blocked' : 'Testing...'}
                  </BrandedBadge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Monitor size={16} />
                    <span className="text-sm">Connection</span>
                  </div>
                  <BrandedBadge 
                    variant={connectionTest === 'success' ? 'success' : connectionTest === 'failed' ? 'error' : 'secondary'}
                    size="small"
                  >
                    {connectionTest === 'success' ? 'Good' : connectionTest === 'failed' ? 'Issues' : 'Testing...'}
                  </BrandedBadge>
                </div>
              </div>

              {(cameraPermission === 'denied' || micPermission === 'denied') && (
                <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="text-sm" style={{ color: 'var(--brand-warning)' }}>
                    Please allow camera and microphone access for the best support experience. 
                    Click the camera icon in your browser's address bar to adjust permissions.
                  </p>
                </div>
              )}
            </BrandedCard>
          </div>

          {/* Right Column - Tips & Info */}
          <div className="space-y-6">
            <BrandedCard>
              <h3 className="font-semibold mb-4" style={{ color: 'var(--brand-text-primary)' }}>
                What to Expect
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start space-x-2">
                  <CheckCircle size={16} className="mt-0.5" style={{ color: 'var(--brand-success)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>
                    A support agent will accept your ticket
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle size={16} className="mt-0.5" style={{ color: 'var(--brand-success)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>
                    You'll automatically join a video session
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle size={16} className="mt-0.5" style={{ color: 'var(--brand-success)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>
                    Agent can share screen and provide guidance
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle size={16} className="mt-0.5" style={{ color: 'var(--brand-success)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>
                    Session typically lasts 15-30 minutes
                  </span>
                </div>
              </div>
            </BrandedCard>

            <BrandedCard>
              <h3 className="font-semibold mb-4" style={{ color: 'var(--brand-text-primary)' }}>
                Need Help Now?
              </h3>
              <div className="space-y-3">
                <BrandedButton variant="outline" size="small" className="w-full justify-start">
                  <FileText size={14} className="mr-2" />
                  Browse Help Articles
                </BrandedButton>
                <BrandedButton variant="outline" size="small" className="w-full justify-start">
                  <MessageCircle size={14} className="mr-2" />
                  Start Live Chat
                </BrandedButton>
                <BrandedButton variant="outline" size="small" className="w-full justify-start">
                  <Phone size={14} className="mr-2" />
                  Call Support: 1-800-SUPPORT
                </BrandedButton>
              </div>
            </BrandedCard>

            <BrandedCard>
              <h3 className="font-semibold mb-4" style={{ color: 'var(--brand-text-primary)' }}>
                Privacy & Security
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-start space-x-2">
                  <Shield size={14} className="mt-0.5" style={{ color: 'var(--brand-success)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>
                    All sessions are encrypted end-to-end
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <Shield size={14} className="mt-0.5" style={{ color: 'var(--brand-success)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>
                    No session recording without consent
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <Shield size={14} className="mt-0.5" style={{ color: 'var(--brand-success)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>
                    Your data is kept confidential
                  </span>
                </div>
              </div>
            </BrandedCard>
          </div>
        </div>
      </main>
    </div>
  );
};